import { FormikProvider, getIn, useFormik } from "formik";
import { IRenter } from "../../../../../../../types/renter.type";
import { useEffect, useMemo, useState } from "react";
import { differenceInDays, formatDate } from "date-fns";
import { AvailableVehiclesFieldFormik } from "../../../../forms/fields/AvailableVehiclesFieldFormik";
import { ImageBondVehicleRenterRow } from "./field/ImageBondVehicleRenterRow";
import { DateSubscriptionFormik } from "../../../../forms/fields/DateSubscriptionFormik";
import ButtonWithLoader from "../../../../../../../common/components/ui/loading/ButtonLoading";
import { unfreezePlanRenter } from "../../../../../../../common/services/renters.api";
import { useToastContext } from "../../../../../../../common/components/ui/toast/toast.context";
import VelotrackActivate from "../../UploadInfoApis/VelotrackActivate";
import ZapSignActivate from "../../UploadInfoApis/ZapSignActivate";
import { useReactivatePlan } from "./customHook/useReactivatePlan";
import { createContract } from "../../../../../services/integration.api";
import ZapSignConfirmation from "../../UploadInfoApis/ZapSignConfirmation";

type Props = {
  data: IRenter;
};

export default function UnfreezePlan(props: Props) {
  const { notifySuccess, notifyError } = useToastContext();

  const { contractData, signatureInfo, handleSignature } = useReactivatePlan(
    props.data
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const renter = props.data;
  let vehicleId;
  let partnerId = renter.activationData.vehicle.partnerId;

  const vehicleHistory = renter?.desactivationData?.vehicleHistory;

  if (vehicleHistory && vehicleHistory.length > 0 && !partnerId) {
    partnerId = vehicleHistory[vehicleHistory.length - 1].partnerId;
  }
  const initialRegisters = [
    { type: "Frente do veículo", content: "" },
    { type: "Lateral direita do veículo", content: "" },
    { type: "Lateral esquerda do veículo", content: "" },
    { type: "Traseira do veículo", content: "" },
    { type: "Odômetro", content: "" },
  ];

  const activeFreezePlan = useMemo(() => {
    if (!renter.freezePlanInfo || !Array.isArray(renter.freezePlanInfo)) {
      return null;
    }

    const activeFreezePlans = renter.freezePlanInfo.filter((freezePlan) => {
      return freezePlan.freezeStatus === "ativo";
    });

    if (activeFreezePlans.length === 0) {
      return null;
    }
    return activeFreezePlans[0];
  }, [renter.freezePlanInfo]);

  const daysRemaining = activeFreezePlan?.freezeStartDate
    ? differenceInDays(new Date(), new Date(activeFreezePlan.freezeStartDate))
    : 0;

  const formik = useFormik({
    initialValues: {
      renterId: renter._id,
      vehicleId,
      registers: initialRegisters,
      initialDateSubscription: new Date(),
      daysRemaining,
    },
    onSubmit: async (values) => {
      try {
        setIsSubmitting(true);
        const result = await unfreezePlanRenter(values);

        if (result) {
          notifySuccess("Plano descongelado com sucesso");
        }
      } catch (error) {
        // Use 'error' em vez de 'result'
        notifyError((error as string) || "Erro ao descongelar plano");
      } finally {
        // Garante que setIsSubmitting(false) seja chamado em todos os casos
        setIsSubmitting(false);
      }
    },
  });

  const vehicle = getIn(formik.values, "vehicle");

  useEffect(() => {
    if (vehicle && vehicle?._id && vehicle?._id !== formik.values.vehicleId) {
      formik.setFieldValue("vehicleId", vehicle?._id);
    }
  }, [vehicle, formik]);

  return (
    <form onSubmit={formik.handleSubmit}>
      <FormikProvider value={formik}>
        <section className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <div>
              <label className="font-semibold text-sm block mb-1">
                Data do congelamento:
              </label>
              <p className="border border-gray h-[43px] text-gray2 p-2 rounded w-full bg-white">
                {activeFreezePlan?.freezeStartDate
                  ? formatDate(activeFreezePlan.freezeStartDate, "dd/MM/yyyy")
                  : "Data não disponível"}
              </p>
            </div>
            <div>
              <label className="font-semibold text-sm block mb-1">
                Motivo:
              </label>
              <p className="border border-gray h-[43px] text-gray2 p-2 rounded bg-white">
                {activeFreezePlan?.freezeReason || "Motivo não disponível"}
              </p>
            </div>
          </div>
          <div className="space-y-2">
            <div>
              <label className="font-semibold text-sm block mb-1">
                Data prevista para descongelar:
              </label>
              <p className="border border-gray h-[43px] text-gray2 p-2 rounded w-full bg-white">
                {activeFreezePlan?.freezeEndDatePlanned
                  ? formatDate(
                    activeFreezePlan.freezeEndDatePlanned,
                    "dd/MM/yyyy"
                  )
                  : "Data não disponível"}
              </p>
            </div>
            <div>
              <label className="font-semibold text-sm block mb-1">
                Dias solicitado para congelar:
              </label>
              <p className="border border-gray h-[43px] text-gray2 p-2 rounded bg-white">
                {activeFreezePlan?.freezeDurationDays ||
                  "Status não disponível"}
              </p>
            </div>
          </div>
        </section>
        <section aria-labelledby="new-plan-section">
          <label className="font-semibold text-sm">
            Selecione o novo veiculo
          </label>
          <div className="grid grid-cols-2 gap-2">
            <DateSubscriptionFormik className="" />

            <AvailableVehiclesFieldFormik
              listAll={false}
              companyId={renter.companyId}
              renterId={renter._id}
              required={true}
            />  
          </div>

          <div className="grid gap-2">
            <h2 className="text-2xl font-semibold mb-4">
              Registros do veículo
            </h2>
            {(!vehicle || !vehicle._id) && (
              <small className="text-redPrimary">
                Selecione um veículo para adicionar as fotos
              </small>
            )}

            {formik.values.registers?.map((doc, index) => (
              <div
                key={index}
                className={`space-x-3 px-3 py-2 my-0 rounded ${index % 2 ? "bg-gray/5" : "bg-gray/25"
                  }`}
              >
                <ImageBondVehicleRenterRow
                  initialData={doc || {}}
                  name={`registers.${index}`}
                  onRemoveClick={() =>
                    formik.setFieldValue(
                      "registers",
                      formik.values.registers.filter((_, i) => i !== index)
                    )
                  }
                  prefix="entrega"
                />
              </div>
            ))}

            <button
              type="button"
              className=" disabled:text-black/50"
              onClick={() => {
                formik.setFieldValue(
                  `registers.${formik.values.registers.length}`,
                  { type: "", content: "", verified: false }
                );
              }}
            >
              + registro
            </button>
          </div>

          <div className="flex justify-end mt-4">
            <ButtonWithLoader
              isLoading={isSubmitting}
              onClick={formik.handleSubmit}
            >
              Salvar
            </ButtonWithLoader>
          </div>
          <div>
            <VelotrackActivate
              onSave={function (): void {
                throw new Error("Function not implemented.");
              }}
              renterId={renter._id!}
            />
          </div>

          <div>
            <ZapSignActivate
              disabled={false}
              onSave={function (): void {
                throw new Error("Function not implemented.");
              }}
              contractData={contractData}
              handleSignature={handleSignature}
              contractFn={createContract}
            />
          </div>
          <div>
            {signatureInfo?.ok && (
              <ZapSignConfirmation contractInfo={signatureInfo} />
            )}
          </div>
        </section>
      </FormikProvider>
    </form>
  );
}
